import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import MODULE_COMPONENTS from './modules'
import PageFrame from './modules/PageFrame'
import useTheme from './hooks/useTheme'
import { interpolate, resolveVariables } from './utils/proposalVariables'
import { isAsNeeded, isIncludedWithPlan } from './utils/frequencyConfig'
import './styles/proposal.css'

// Modules that fill exactly one page
const FIXED_PAGE_MODULES = new Set(['cover'])

// Fallback image map (same as ProposalPreview)
const DEFAULT_IMAGE_MAP = {
  'basic maintenance': '/build/images/proposals/edging.webp',
  irrigation: '/build/images/proposals/sprinklers.webp',
  trimming: '/build/images/proposals/trimming_close.webp',
  'mowing (if applicable)': '/build/images/proposals/edging.webp',
  mowing: '/build/images/proposals/edging.webp',
  'weed control & pre-emergent': '/build/images/proposals/spray_close.webp',
  'weed control': '/build/images/proposals/spray_close.webp',
  'plant replacement': '/build/images/proposals/tree_trim_close.webp',
  'pest control': '/build/images/proposals/pest_control.webp',
  'arbor care': '/build/images/proposals/tree_trim_close.webp',
}

// This is a public page (PDF rendering, customer-shared links).
// Always use relative API paths — no Redux dependency.
const PreviewOnlyPage = () => {
  const { id } = useParams()
  const [proposal, setProposal] = useState(null)
  const [template, setTemplate] = useState(null)
  const [moduleDefaults, setModuleDefaults] = useState({})

  useEffect(() => {
    axios.get('/api/mtc-proposals/module-registry')
      .then(res => {
        if (Array.isArray(res.data)) {
          const map = {}
          res.data.forEach(m => { map[m.id] = m.defaults || {} })
          setModuleDefaults(map)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!id) return
    axios.get(`/api/mtc-proposals/${id}`)
      .then(res => {
        setProposal(res.data)
        setTemplate(res.data.templateId || null)
      })
      .catch(err => console.error('Failed to load proposal for preview:', err))
  }, [id])

  useTheme(template?.theme)

  const variables = useMemo(() => {
    return resolveVariables(template, proposal)
  }, [template, proposal])

  if (!proposal) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>
  }

  const sortedModules = [...(proposal.modules || [])].sort((a, b) => a.order - b.order)
  const crmSnapshot = proposal.crmSnapshot || {}

  // Build pages — same logic as ProposalPreview
  const pages = []

  sortedModules.forEach((mod) => {
    if (!mod.enabled) return
    const Component = MODULE_COMPONENTS[mod.moduleId]
    if (!Component) return

    const defaults = moduleDefaults[mod.moduleId] || {}
    const data = { ...defaults, ...(mod.overrides || {}) }

    // Skip serviceArea if there's no map or property data to show
    if (mod.moduleId === 'serviceArea' && !data.mapImageUrl && !(data.propertyLines && data.propertyLines.length)) return

    const extraProps = {}

    if (mod.moduleId === 'project') {
      const scopeDefaults = moduleDefaults.scope || {}
      const scopeMod = proposal.modules?.find(m => m.moduleId === 'scope')
      extraProps.scopeData = { ...scopeDefaults, ...(scopeMod?.overrides || {}) }
    }

    if (FIXED_PAGE_MODULES.has(mod.moduleId)) {
      pages.push({
        key: mod.moduleId,
        fixed: true,
        noFrame: true,
        items: [{ mod, Component, data, extraProps }]
      })
    } else if (mod.moduleId === 'scope') {
      pages.push({
        key: 'scope-table',
        fixed: false,
        items: [{ mod, Component, data, extraProps, renderMode: 'table-only' }]
      })

      const visibleServices = (data?.services || []).filter(svc => !svc.hidden && !svc.hideDetailPage && svc.description)
      visibleServices.forEach((svc, idx) => {
        pages.push({
          key: `service-detail-${idx}`,
          fixed: true,
          serviceDetail: { svc, idx }
        })
      })
    } else {
      pages.push({
        key: mod.moduleId,
        fixed: false,
        items: [{ mod, Component, data, extraProps }]
      })
    }
  })

  // Calculate page numbers (skip cover)
  let pageNum = 0
  const totalPages = pages.filter(p => !p.noFrame).length

  return (
    <div className="proposal-preview-only">
      {pages.map((page) => {
        if (!page.noFrame) pageNum++

        if (page.serviceDetail) {
          const { svc, idx } = page.serviceDetail
          const img = svc.imageUrl || DEFAULT_IMAGE_MAP[svc.name?.toLowerCase()] || ''
          const isAlt = idx % 2 === 1
          const highlights = svc.highlights || []
          const heroHighlight = highlights[0] || null
          const gridHighlights = highlights.slice(1)
          const stats = svc.stats || []
          const checklist = svc.checklist || []
          const callout = svc.callout || null
          const highlights2 = svc.highlights2 || []

          let className = 'proposal-service-detail'
          if (!img) className += ' proposal-service-detail--no-image'
          else if (isAlt) className += ' proposal-service-detail--alt'

          return (
            <div key={page.key} className="proposal-page proposal-page--fixed">
              <PageFrame theme={template?.theme} pageNumber={pageNum} totalPages={totalPages} entityNameFormal={variables?.entityNameFormal}>
                <div className={className}>
                  {img && (
                    <div className="proposal-service-detail__image">
                      <img src={img} alt={svc.name} />
                    </div>
                  )}
                  <div className="proposal-service-detail__content">
                    <div className="proposal-service-detail__title">{svc.name}</div>
                    {isAsNeeded(svc.frequency) && isIncludedWithPlan(svc.frequency) === true && (
                      <div className="proposal-service-detail__inclusion-badge proposal-service-detail__inclusion-badge--included">
                        Included w/ Mtc Plan
                      </div>
                    )}
                    {isAsNeeded(svc.frequency) && isIncludedWithPlan(svc.frequency) === false && (
                      <div className="proposal-service-detail__inclusion-badge proposal-service-detail__inclusion-badge--additional">
                        Additional Costs May Apply
                      </div>
                    )}
                    <div className="proposal-service-detail__accent-line" />
                    <div className="proposal-service-detail__body">{interpolate(svc.description, variables)}</div>

                    {stats.length > 0 && (
                      <div className="proposal-service-detail__stats">
                        {stats.map((stat, i) => (
                          <div className="proposal-service-detail__stat" key={i}>
                            <div className="proposal-service-detail__stat-value">{interpolate(stat.value, variables)}</div>
                            <div className="proposal-service-detail__stat-label">{interpolate(stat.label, variables)}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {heroHighlight && (
                      <div className="proposal-service-detail__hero">
                        <div className="proposal-service-detail__hero-title">{interpolate(heroHighlight.title, variables)}</div>
                        <div className="proposal-service-detail__hero-text">{interpolate(heroHighlight.text, variables)}</div>
                      </div>
                    )}

                    {checklist.length > 0 && (
                      <div className="proposal-service-detail__checklist">
                        {checklist.map((item, i) => (
                          <div className="proposal-service-detail__check-item" key={i}>
                            <span className="proposal-service-detail__check-icon">&#10003;</span>
                            <span>{interpolate(item, variables)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {callout && (
                      <div className="proposal-service-detail__callout">
                        <div className="proposal-service-detail__callout-title">{interpolate(callout.title, variables)}</div>
                        <div className="proposal-service-detail__callout-text">{interpolate(callout.text, variables)}</div>
                      </div>
                    )}

                    {gridHighlights.length > 0 && (
                      <div className="proposal-service-detail__highlights">
                        {gridHighlights.map((h, i) => (
                          <div className="proposal-service-detail__highlight" key={i}>
                            <div className="proposal-service-detail__highlight-title">{interpolate(h.title, variables)}</div>
                            <div className="proposal-service-detail__highlight-text">{interpolate(h.text, variables)}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {highlights2.length > 0 && (
                      <div className="proposal-service-detail__highlights">
                        {highlights2.map((h, i) => (
                          <div className="proposal-service-detail__highlight" key={`h2-${i}`}>
                            <div className="proposal-service-detail__highlight-title">{interpolate(h.title, variables)}</div>
                            <div className="proposal-service-detail__highlight-text">{interpolate(h.text, variables)}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {svc.footnote && (
                      <div className="proposal-service-detail__footnote">{interpolate(svc.footnote, variables)}</div>
                    )}
                  </div>
                </div>
              </PageFrame>
            </div>
          )
        }

        const pageClass = page.fixed ? 'proposal-page proposal-page--fixed' : 'proposal-page proposal-page--flow'

        const content = page.items.map(({ mod, Component, data, extraProps, renderMode }) => (
          <Component
            key={mod.moduleId}
            data={data}
            theme={template?.theme}
            crmSnapshot={crmSnapshot}
            variables={variables}
            renderMode={renderMode}
            {...extraProps}
          />
        ))

        return (
          <div key={page.key} className={pageClass}>
            {page.noFrame ? (
              content
            ) : (
              <PageFrame theme={template?.theme} pageNumber={pageNum} totalPages={totalPages} entityNameFormal={variables?.entityNameFormal}>
                {content}
              </PageFrame>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default PreviewOnlyPage
